import { useMemo } from 'react';
import { useDroppable } from '@dnd-kit/core';
import cn from 'classnames';
import { useDraggableRowStyle } from '../../lib/hooks/useDraggableRowStyle';
import { isDroppableOverDataNode } from '../../lib/isDroppableOverData';
import { type DroppableOverData, type TreeRow } from '../../lib/types';
import stylesDroppableSlot from './DroppableSlot.module.css';

interface DroppableSlotProps {
    isHighlightByParent?: boolean;
    size: 's' | 'm';
    isDroppable?: boolean;
    node?: TreeRow;
}

export const DroppableSlot = ({ isDroppable = true, isHighlightByParent = false, node, size }: DroppableSlotProps) => {
    const { isOver, setNodeRef, active, over } = useDroppable({
        id: node ? `${isHighlightByParent ? 'light' : 'delimiter'}-${node.id}` : 'end',
        data: { type: 'delimiter', node } satisfies DroppableOverData,
        disabled: !isDroppable || isHighlightByParent,
    });

    const overData = over?.data.current;

    const isOverByEndOfParent =
        isHighlightByParent &&
        node &&
        isDroppableOverDataNode(overData) &&
        overData.node.childrenIds.has(node.id) &&
        node.isLastInParent &&
        node.level === overData.node.level + 1;

    const additionalStyle = useMemo(() => {
        if (isOverByEndOfParent) {
            return {
                /**
                 * Отступ для показа в какую позицию и на какой уровень попадет активная строка при дропе
                 * 40 - ширина кнопки/иконки перемещения
                 * 32 - ширина отрисовки уровней (либо иконка раскрытия/просто линии)
                 */
                marginLeft: 40 + 32 * (overData?.node?.level + 1),
            };
        }

        return undefined;
    }, [isOverByEndOfParent, overData?.node?.level]);

    const style = useDraggableRowStyle(node, active, over, { additionalStyle, delta: -1, size });

    return (
        <div
            className={cn(stylesDroppableSlot.container, { [stylesDroppableSlot.over]: isOver || isOverByEndOfParent })}
            style={style}
            ref={setNodeRef}
        />
    );
};
